import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { immer } from 'zustand/middleware/immer';
import { ChannelModel, EsportChannelModel, streamingService } from '@/services/streaming.service';
import { SportMatchModel } from '@nf/types/odds';
import { UserInfo } from '@nf/types/account';
import { SiteConfigsSettingsData } from '@nf/types/site';
import { EsportStreamingUrlResponse } from '@/hooks/query/use-esport-streaming-data';

export type StreamingType = 'GV' | 'Streaming' | 'Streamer' | 'StaticInfo' | 'SlmtGV' | '';
export interface StreamingStore {
	isRefresh: boolean;
	setIsRefresh: (isRefresh: boolean) => void;
	isLoading: boolean;
	setIsLoading: (isLoading: boolean) => void;
	isClose: boolean;
	setIsClose: (isClose: boolean) => void;
	isMiniStreaming: boolean;
	setIsMiniStreaming: (isMini: boolean) => void;
	streamingMatchId: number;
	setStreamingMatchId: (matchId: number) => void;
	streamingUrl: string;
	setStreamingUrl: (url: string) => void;
	streamingType: StreamingType;
	setStreamingType: (type: StreamingType) => void;
	streamingSrc: string;
	setStreamingSrc: (src: string) => void;
	channelList: Array<ChannelModel>;
	setChannelList: (channelList: Array<ChannelModel>) => void;
	esportchannelList: Array<EsportChannelModel>;
	setEsportChannelList: (channelList: Array<EsportChannelModel>) => void;
	setStreamingStatus: (type: StreamingType, src: string, channelModel: ChannelModel[]) => void;
	setChangeMatchStatus: (resourceList: Array<string>, dmatch: SportMatchModel, isInit: boolean, siteconfigsData?: SiteConfigsSettingsData | null, accountInfo?: UserInfo | null, esportStreamingSource?: EsportStreamingUrlResponse[] | null | undefined) => void;
	setSwitchChannel: (src: string) => void;
	isPin: boolean;
	setIsPin: (isPin: boolean) => void;
	isStreamingFloat: boolean;
	setIsStreamingFloat: (streamingFloat: boolean) => void;
	videoSpecial: boolean;
	setVideoSpecial: (videoSpecial: boolean) => void;
	isExpnadChatroom: boolean;
	setIsExpnadChatroom: (isReady: boolean) => void;
	chatroomUrl: string;
	setChatroomUrl: (src: string) => void;
	streamingPluginUrl: string;
	setStreamingPluginUrl: (PluginUrl: string) => void;
	mcdnClientId: string;
	setMcdnClientId: (id: string) => void;
	isEsport: boolean;
	setIsEsport: (isEsport: boolean) => void;
	esportSrc: string;
	setEsportSrc: (src: string) => void;
}

export const useStreamingStore = createWithEqualityFn<StreamingStore, [['zustand/immer', never]]>(
	immer<StreamingStore>((set, get) => ({
		isRefresh: false,
		setIsRefresh: isRefresh => set(_ => ({
			isRefresh: isRefresh
		})),
		isLoading: false,
		setIsLoading: isLoading => set(_ => ({
			isLoading: isLoading
		})),
		isClose: false,
		setIsClose: isClose => set(_ => ({
			isClose: isClose
		})),
		isMiniStreaming: false,
		setIsMiniStreaming: isMini => set(_ => ({ isMiniStreaming: isMini })),
		streamingMatchId: 0,
		setStreamingMatchId: matchId => set(_ => ({
			streamingMatchId: matchId
		})),
		streamingUrl: '',
		setStreamingUrl: url => set(_ => ({
			streamingUrl: url
		})),
		streamingType: '',
		setStreamingType: type => set(_ => ({
			streamingType: type
		})),
		streamingSrc: '',
		setStreamingSrc: src => set(_ => ({
			streamingSrc: src
		})),
		channelList: [],
		setChannelList: (channelList: ChannelModel[]) => {
			set(params => {
				params.channelList = [...channelList];
			})
		},
		esportchannelList: [],
		setEsportChannelList: (esportchannelList: EsportChannelModel[]) => {
			set(params => {
				params.esportchannelList = [...esportchannelList];
			})
		},
		setStreamingStatus: (type: StreamingType, src: string, channelModel: ChannelModel[]) => {
			set(params => {
				params.streamingType = type;
				params.streamingSrc = src;
				params.channelList = [...channelModel];
			})
		},
		setChangeMatchStatus: (resourceList: Array<string>, match: SportMatchModel, isInit: boolean, siteconfigsData?: SiteConfigsSettingsData | null, accountInfo?: UserInfo | null, esportStreamingSource?: EsportStreamingUrlResponse[] | null | undefined) => {
			let isMatchChange = false;
			if (!match?.MatchId ||
				(isInit &&
					get().streamingType &&
					get().streamingMatchId === match.MatchId &&
					((get().streamingType !== 'StaticInfo' && get().streamingSrc && get().channelList.length > 0) ||
						(get().streamingType === 'StaticInfo'))
				)) {
				return;
			} else {
				if (isInit) {
					set({ streamingMatchId: match.MatchId });
					set({ streamingSrc: '' });
					isMatchChange = true;
				}
			}
			// Esport
			if (match.GameID === 43) {
				const videoInfo = streamingService.getVideoInfo(match, siteconfigsData, accountInfo)?.src;
				if (videoInfo && streamingService.EsportStreamingSrc.indexOf(videoInfo) < 0) {
					set({ isEsport: false });
					// set({ esportSrc: '' });
					// set({ esportchannelList: [] });
				} else {
					set({ isEsport: true });
					// set({ esportSrc: '' });
					// set({ esportchannelList: [] });
					return;
				}
			}
			set({ isEsport: false });
			set({ esportchannelList: [] });
			set({ esportSrc: '' });
			let channelList = get().channelList;
			channelList = !isMatchChange && channelList.length > 0 ? channelList : streamingService.getChannelList(resourceList, match, siteconfigsData, accountInfo);
			set({ channelList: channelList });
			const streamingType = get().streamingType;
			if (streamingType && channelList.length > 1) {
				let channelIndex = 0;
				switch (streamingType) {
					case 'GV':
						return;
					case 'Streamer':
						channelIndex = channelList.findIndex(channel => streamingService.LiveStreamerSrc.indexOf(Number(channel.value.split('-')[0])) > -1);
						break;
					case 'Streaming':
						channelIndex = channelList.findIndex(channel => streamingService.LiveStreamerSrc.indexOf(Number(channel.value.split('-')[0])) === -1);
						break;
				}
				if (channelList[channelIndex]?.value) {
					set({ streamingSrc: channelList[channelIndex].value });
				}
			} else if (streamingType && channelList.length > 0) {
				set({ streamingSrc: channelList[0].value });
			}
			set({ streamingUrl: '' });
		},
		setSwitchChannel: (src: string) => {
			if (streamingService.LiveStreamerSrc.indexOf(Number(src.split('-')[0])) > -1) {
				set({ streamingType: 'Streamer' });
			} else {
				set({ streamingType: 'Streaming' });
			}
			set({ streamingSrc: src });
			set({ streamingUrl: '' });
		},
		isPin: false,
		setIsPin: isPin => set(_ => ({
			isPin: isPin
		})),
		isStreamingFloat: false,
		setIsStreamingFloat: isStreamingFloat => set(_ => ({
			isStreamingFloat: isStreamingFloat
		})),
		videoSpecial: false,
		setVideoSpecial: videoSpecial => set(_ => ({
			videoSpecial: videoSpecial
		})),
		isExpnadChatroom: false,
		setIsExpnadChatroom: isExpand => set(_ => ({
			isExpnadChatroom: isExpand
		})),
		chatroomUrl: '',
		setChatroomUrl: src => set(_ => ({
			chatroomUrl: src
		})),
		streamingPluginUrl: '',
		setStreamingPluginUrl: PluginUrl => set(_ => ({
			streamingPluginUrl: PluginUrl
		})),
		mcdnClientId: '',
		setMcdnClientId: id => set(_ => ({
			mcdnClientId: id
		})),
		isEsport: false,
		setIsEsport: Esport => set(_ => ({
			isEsport: Esport
		})),
		esportSrc: '',
		setEsportSrc: src => set(_ => ({
			esportSrc: src
		})),
	})),
	shallow
);